import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowBack,
  FilterAlt,
  FormatListBulleted,
  Search,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import { useListResourceTypesQuery } from '../store/hooks';

import { ResourceDetailView } from './resource-detail-view';
import { ResourceListView } from './resource-list-view';

const PER_PAGE = 25;

interface ResourceCapability {
  org?: boolean;
  user?: boolean;
  patient?: boolean;
}

interface ResourceTypeRow {
  resource_type: string;
  model?: string;
  scope?: string;
  capability?: ResourceCapability | null;
}

interface ResourceExplorerSectionProps {
  organizationId: string;
}

/**
 * Resource Explorer (GDN-1924), rendered inline inside the organization chrome.
 *
 * Three views, all addressable via the org page's query params so Back / refresh
 * / deep-link behave — and the org tabs never disappear:
 *   - no `rtype`            → catalog (open-by-id + registered-type table)
 *   - `rtype`              → owner-scoped list for that type (GDN-1922)
 *   - `rtype` + `rid`      → that resource's detail
 */
export function ResourceExplorerSection({
  organizationId,
}: ResourceExplorerSectionProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading } = useListResourceTypesQuery();
  const resources = (data?.items ?? []) as ResourceTypeRow[];

  const rtype = searchParams.get('rtype') ?? '';
  const rid = searchParams.get('rid') ?? '';
  const rpatient = searchParams.get('rpatient') ?? undefined;
  const rprovider = searchParams.get('rprovider') ?? undefined;
  const rpage = Math.max(1, Number(searchParams.get('rpage') ?? '1') || 1);

  // Patch only the explorer's own params; preserve tab/sub (and anything else)
  // so we stay on the org route with its chrome intact.
  const patch = (changes: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(changes)) {
      if (value === null) next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next);
  };

  const toCatalog = () =>
    patch({
      rtype: null,
      rid: null,
      rpatient: null,
      rprovider: null,
      rpage: null,
    });
  const toList = () => patch({ rid: null });

  if (rtype && rid) {
    return (
      <Box sx={{ mb: 3 }}>
        <Button onClick={toList} startIcon={<ArrowBack />} sx={{ mb: 2 }}>
          Back to {rtype} list
        </Button>
        <ResourceDetailView
          resourceType={rtype}
          resourceId={rid}
          organizationId={organizationId}
        />
      </Box>
    );
  }

  if (rtype) {
    const capability =
      resources.find(r => r.resource_type === rtype)?.capability ?? undefined;
    return (
      <Box sx={{ mb: 3 }}>
        <Button onClick={toCatalog} startIcon={<ArrowBack />} sx={{ mb: 2 }}>
          Back to explorer
        </Button>
        <ExplorerListPanel
          resourceType={rtype}
          organizationId={organizationId}
          capability={capability}
          patientId={rpatient}
          providerId={rprovider}
          page={rpage}
          onApply={(patient, provider) =>
            patch({
              rpatient: patient || null,
              rprovider: provider || null,
              rpage: null,
            })
          }
          onPageChange={p => patch({ rpage: String(p) })}
          onOpen={id => patch({ rid: id })}
        />
      </Box>
    );
  }

  return (
    <ExplorerCatalog
      resources={resources}
      isLoading={isLoading}
      onOpenById={(type, id) => patch({ rtype: type, rid: id })}
      onBrowse={type =>
        patch({
          rtype: type,
          rid: null,
          rpatient: null,
          rprovider: null,
          rpage: null,
        })
      }
    />
  );
}

interface ExplorerCatalogProps {
  resources: ResourceTypeRow[];
  isLoading: boolean;
  onOpenById: (resourceType: string, resourceId: string) => void;
  onBrowse: (resourceType: string) => void;
}

function ExplorerCatalog({
  resources,
  isLoading,
  onOpenById,
  onBrowse,
}: ExplorerCatalogProps) {
  const [type, setType] = useState('');
  const [id, setId] = useState('');

  const open = () => {
    if (!type || !id.trim()) return;
    onOpenById(type, id.trim());
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Resource Explorer"
        subheader="Inspect any resource on the common interface. Open one by type + id (e.g. a migration committed_resource_id), or browse a listable type filtered by patient / provider."
      />
      <CardContent>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Resource type</InputLabel>
            <Select
              label="Resource type"
              value={type}
              onChange={e => setType(e.target.value)}
            >
              {resources.map(r => (
                <MenuItem key={r.resource_type} value={r.resource_type}>
                  {r.resource_type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Resource id"
            value={id}
            onChange={e => setId(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') open();
            }}
            sx={{ flex: 1 }}
          />
          <Button
            variant="contained"
            startIcon={<Search />}
            disabled={!type || !id.trim()}
            onClick={open}
          >
            Open
          </Button>
        </Box>

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Registered resource types
        </Typography>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Resource type</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Scope</TableCell>
                <TableCell>List</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {resources.map(r => (
                <TableRow
                  key={r.resource_type}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setType(r.resource_type)}
                >
                  <TableCell>{r.resource_type}</TableCell>
                  <TableCell>{r.model}</TableCell>
                  <TableCell>
                    {r.scope ? <Chip size="small" label={r.scope} /> : '—'}
                  </TableCell>
                  <TableCell>
                    {r.capability ? (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap',
                        }}
                      >
                        {r.capability.org && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<FormatListBulleted />}
                            onClick={e => {
                              e.stopPropagation();
                              onBrowse(r.resource_type);
                            }}
                          >
                            Org
                          </Button>
                        )}
                        {r.capability.user && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={e => {
                              e.stopPropagation();
                              onBrowse(r.resource_type);
                            }}
                          >
                            By provider
                          </Button>
                        )}
                        {r.capability.patient && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={e => {
                              e.stopPropagation();
                              onBrowse(r.resource_type);
                            }}
                          >
                            By patient
                          </Button>
                        )}
                      </Box>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

interface ExplorerListPanelProps {
  resourceType: string;
  organizationId: string;
  capability?: ResourceCapability;
  patientId?: string;
  providerId?: string;
  page: number;
  onApply: (patientId: string, providerId: string) => void;
  onPageChange: (page: number) => void;
  onOpen: (resourceId: string) => void;
}

function ExplorerListPanel({
  resourceType,
  organizationId,
  capability,
  patientId,
  providerId,
  page,
  onApply,
  onPageChange,
  onOpen,
}: ExplorerListPanelProps) {
  const canPatient = capability?.patient ?? false;
  const canProvider = capability?.user ?? false;

  const [patientInput, setPatientInput] = useState(patientId ?? '');
  const [providerInput, setProviderInput] = useState(providerId ?? '');

  const apply = () =>
    onApply(
      canPatient ? patientInput.trim() : '',
      canProvider ? providerInput.trim() : '',
    );

  return (
    <Card>
      <CardHeader
        title={resourceType}
        subheader="Owner-scoped list via the common interface — filter by the applicable owner, then open a row for the full record."
      />
      <CardContent>
        {(canPatient || canProvider) && (
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
            {canPatient && (
              <TextField
                size="small"
                label="Patient id"
                value={patientInput}
                onChange={e => setPatientInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') apply();
                }}
              />
            )}
            {canProvider && (
              <TextField
                size="small"
                label="Provider id"
                value={providerInput}
                onChange={e => setProviderInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') apply();
                }}
              />
            )}
            <Button
              variant="outlined"
              startIcon={<FilterAlt />}
              onClick={apply}
            >
              Apply
            </Button>
          </Box>
        )}
        <ResourceListView
          resourceType={resourceType}
          organizationId={organizationId}
          patientId={canPatient ? patientId : undefined}
          providerId={canProvider ? providerId : undefined}
          page={page}
          perPage={PER_PAGE}
          onPageChange={onPageChange}
          onOpenResource={onOpen}
        />
      </CardContent>
    </Card>
  );
}
